import type { DeathStats, OverviewSnapshot } from '@kenocyber/game-domain';
import { type CardSlotUiView, cardSlotUiViews } from '@kenocyber/game-domain';
import type { AssetManifest, CannonRarity } from '@kenocyber/shared';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { DEV_TOOLS_ALLOWED, NO_DEV_FLAGS, readDevFlags } from './dev-tools';
import { fetchAssetManifest } from './game/assets';
import { createGameBridge, type DevCommand } from './game/bridge';
import { PhaserHost } from './game/PhaserHost';
import { PauseCoordinator } from './game/pause-coordinator';
import { useHuntSession } from './game/useHuntSession';
import { attachVisibilityPause } from './game/visibility';
import { OpenWorldFeature, OpenWorldOpenButton } from './open-world/OpenWorldFeature';
import { type OpenWorldHuntLink, useOpenWorld } from './open-world/useOpenWorld';
import { PvpFeature, PvpOpenButton } from './pvp/PvpFeature';
import { CardHudBar } from './ui/CardHudBar';
import { DeathScreen } from './ui/DeathScreen';
import { DevPanel } from './ui/DevPanel';
import { CardDropToasts, DropToasts } from './ui/DropToast';
import { EquipmentDropToasts } from './ui/EquipmentDropToast';
import { type FloatingOverlay, FloatingOverlays, toggleOverlay } from './ui/FloatingOverlays';
import { HudPanel } from './ui/HudPanel';
import { huntOverlayView } from './ui/hunt-overlays';
import { InventoryPanel } from './ui/InventoryPanel';
import { MapsPanel } from './ui/MapsPanel';
import { MetricsPanel } from './ui/MetricsPanel';
import { MiningPanel } from './ui/MiningPanel';
import { OfflineRewardDialog } from './ui/OfflineRewardDialog';
import { OverviewPanel } from './ui/OverviewPanel';
import { TopBar } from './ui/TopBar';

/**
 * The 3D pet overlay.
 *
 * Pets are 3D officially now, so this ships in production - but LAZILY, and
 * that distinction is the whole point: the `import()` puts Three.js and the
 * models in their own chunks, and the render site below mounts nothing until
 * an account actually has a pet active. A player who has not obtained one
 * downloads exactly as much as they did before pets existed.
 */
const Pet3DOverlay = lazy(async () => {
  const overlay = await import('./game/pet3d/Pet3DOverlay');
  return { default: overlay.Pet3DOverlay };
});

interface DeathInfo {
  stats: DeathStats;
  cause: 'boss-breach' | 'mob-leaks';
}

interface AppProps {
  /** Public name of the authenticated player (AuthGate guarantees a session). */
  readonly playerName: string;
  /**
   * Who the player IS, as opposed to what they are called.
   *
   * Immutable for the life of the account, so anything that must not survive a
   * change of player keys on it. A display name cannot: two accounts may share
   * one, and one account may change its own.
   */
  readonly accountId: string;
  readonly onSignOut: () => void;
}

export function App({ playerName, accountId, onSignOut }: AppProps) {
  // One bridge for the app's lifetime. Stable identity is what lets PhaserHost
  // guarantee the canvas is never recreated by a React re-render.
  const bridge = useMemo(() => createGameBridge(), []);
  // A URL alone can never enable tooling: the build must allow it too.
  // Written as a literal-guarded ternary (not an argument) so the bundler can
  // fold it away entirely when DEV_TOOLS_ALLOWED is the constant `false`.
  const flags = useMemo(
    () => (DEV_TOOLS_ALLOWED ? readDevFlags(window.location.search, true) : NO_DEV_FLAGS),
    [],
  );

  const [manifest, setManifest] = useState<AssetManifest | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<OverviewSnapshot | null>(null);
  /**
   * Os três espaços, derivados da INSTANTÂNEA a cada empurrão do overview.
   *
   * Da instantânea determinística e não de um estado próprio: os relógios das
   * cartas vivem na simulação, e uma segunda cópia deles aqui teria de ser
   * mantida sincronizada por alguém - que é como uma barra passa a mostrar um
   * arrefecimento que já acabou.
   *
   * O empurrão é regulado a 250 ms, o que é folgado para um contador de
   * segundos e é a razão de isto não ser uma mensagem por quadro.
   */
  const [cardSlots, setCardSlots] = useState<readonly CardSlotUiView[]>([]);
  const [fps, setFps] = useState(0);
  /*
   * Whether the arena's tab row is showing. Open by default: the tabs are how
   * a player reaches everything outside the hunt, so hiding them by default
   * would hide the game. Presentation only - nothing here touches the session.
   */
  const [tabsOpen, setTabsOpen] = useState(true);
  const [rapidFire, setRapidFire] = useState(false);
  const [death, setDeath] = useState<DeathInfo | null>(null);
  /** A scene-level failure, surfaced instead of a silently stopped arena. */
  const [sceneError, setSceneError] = useState<string | null>(null);
  // The Overview starts closed and floats; it never reserves layout space.
  const [overviewOpen, setOverviewOpen] = useState(false);
  // The inventory floats too, over a hunt that keeps running behind it.
  const [inventoryOpen, setInventoryOpen] = useState(false);
  /** Bumped after a confirmed equip so the panel reloads from the server. */
  const [inventoryRefresh, setInventoryRefresh] = useState(0);
  /**
   * Which of the exclusive panels is open, if any.
   *
   * ONE value rather than one boolean each. The trade, the marketplace and the
   * gacha are alternatives - a player is doing one of them - and independent
   * booleans let two be true at once, which mounted both panels on top of each
   * other with duplicated backdrops and close buttons. A union type has no such
   * state, and adding a third member kept it that way for free. All three talk
   * only to the API: no bridge message, no simulation state, and nothing that
   * could pause or restart the hunt.
   */
  const [overlay, setOverlay] = useState<FloatingOverlay | null>(null);
  /**
   * Set when 3D cannot run at all - no WebGL, a lost context, a model that
   * failed to build.
   *
   * There is no 2D MODE to fall back to: pets are 3D. What this drives is an
   * accessibility fallback, a still thumbnail in place of the live model, and
   * a message saying why.
   */
  const [petRenderError, setPetRenderError] = useState<string | null>(null);

  // The authoritative session: XP, Gold, level and points all live server-side
  // and reach the scene only after the server authorised them.
  const hunt = useHuntSession(bridge, flags.debug);

  useEffect(() => {
    let cancelled = false;
    fetchAssetManifest()
      .then((loaded) => {
        if (!cancelled) {
          setManifest(loaded);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setManifestError(error instanceof Error ? error.message : String(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const offSnapshot = bridge.onUiMessage('hunt:snapshot', (message) => {
      setSnapshot(message.snapshot);
      setFps(message.fps);
      setRapidFire(message.rapidFire);
      setCardSlots(
        cardSlotUiViews({
          cards: message.deterministicSnapshot.cards,
          shipAlive: message.deterministicSnapshot.phase !== 'dead',
          hasSession: true,
          mode: 'hunt',
        }),
      );
    });
    const offDeath = bridge.onUiMessage('hunt:death', (info) => {
      setDeath(info);
    });
    const offRestarted = bridge.onUiMessage('hunt:restarted', () => {
      setDeath(null);
    });
    // A scene that cannot adopt a session must say so. Silently leaving a
    // stopped arena on screen is the one outcome that reads as "the game
    // broke and I have no idea why".
    const offSceneError = bridge.onUiMessage('hunt:scene-error', (failure) => {
      setSceneError(`${failure.stage}: ${failure.message}`);
    });
    return () => {
      offSnapshot();
      offDeath();
      offRestarted();
      offSceneError();
    };
  }, [bridge]);

  /**
   * The one thing that decides whether the hunt is paused.
   *
   * A hidden tab grants exactly zero progress, and so does a duel - but those
   * are two reasons rather than one boolean, and the last publisher used to
   * win. The coordinator ORs every reason and is the only publisher of
   * `ui:set-paused`; visibility and the duel each hold a lease of their own.
   *
   * Memoised on the bridge, like every other long-lived listener here, so a
   * re-render never produces a second coordinator with its own opinion.
   */
  const pause = useMemo(() => new PauseCoordinator(bridge), [bridge]);
  /*
   * A caça, pela porta estreita que o mundo aberto usa (gate V28I.BZ): suspender
   * ao partir, retomar por UM bootstrap ao voltar, e a palavra do servidor sobre
   * onde a conta está.
   */
  const huntLink = useMemo<OpenWorldHuntLink>(
    () => ({
      suspended: hunt.suspended,
      suspendedFor: hunt.suspendedFor,
      suspend: hunt.suspendForOpenWorld,
      resume: hunt.resumeFromOpenWorld,
    }),
    [hunt.suspended, hunt.suspendedFor, hunt.suspendForOpenWorld, hunt.resumeFromOpenWorld],
  );
  /*
   * A expedição. Dona do socket, do bilhete e da travessia; a cena é dona do
   * teclado, do rato e da câmara. Os dois falam pela ponte tipada.
   */
  const openWorld = useOpenWorld(bridge, pause, huntLink);
  const [openWorldPanel, setOpenWorldPanel] = useState(false);
  /** Está uma expedição no ecrã. A caça está parada, escondida, e atrás dela. */
  const openWorldActive =
    openWorld.state.phase === 'live' ||
    openWorld.state.phase === 'crossing' ||
    openWorld.state.phase === 'reconnecting' ||
    openWorld.state.receipt !== null;
  /*
   * **A caça fora do ecrã**: uma expedição nele, ou a caça suspensa por uma
   * (gate V28I.BZ). Tudo o que descreve a caça — o HUD, as abas, as fichas, os
   * avisos — sai pelas duas razões, porque as duas querem dizer o mesmo: não há
   * caçada nenhuma a correr para descrever.
   */
  const huntOffScreen = openWorldActive || hunt.suspended;
  /*
   * **Suspensa e sem nada do mundo aberto no ecrã, o painel abre** — e não
   * fecha enquanto for assim. É a única coisa que resta fazer: retomar a
   * expedição que a autoridade diz existir.
   */
  useEffect(() => {
    if (openWorld.stranded && !openWorldPanel) {
      setOpenWorldPanel(true);
    }
  }, [openWorld.stranded, openWorldPanel]);
  /*
   * **Partir fecha o painel de partida.**
   *
   * Ele fica escondido por baixo da expedição enquanto ela dura — o anfitrião
   * mostra o HUD em vez dele — e sem isto reapareceria no instante em que a
   * pessoa voltasse da caça, a oferecer a viagem seguinte antes de ela ter
   * olhado para onde chegou. Fechá-lo à partida é o que faz o regresso
   * aterrar na caça e em mais nada.
   */
  useEffect(() => {
    if (openWorldActive) {
      setOpenWorldPanel(false);
    }
  }, [openWorldActive]);

  // Registered once for the app's lifetime and torn down on unmount, so
  // listeners never accumulate.
  useEffect(() => attachVisibilityPause(bridge, pause, document, window), [bridge, pause]);

  const sendDevCommand = (command: DevCommand) => {
    bridge.sendToGame('hunt:dev', { command });
  };

  /**
   * 3D could not run. Show the still thumbnail in its place and say why.
   *
   * In place: no restart, no reload, no lost session. The hunt carries on with
   * the pet fighting exactly as it was - only its picture changed.
   */
  const petRenderUnavailable = useCallback((reason: string) => {
    setPetRenderError(reason);
  }, []);

  /*
   * The ring follows the overlay STATE, not the button that opened it.
   *
   * Telling the scene from the open button covered exactly one of the ways
   * this panel closes: the × and `Escape` left the ring drawn over the arena,
   * because nothing had told it otherwise. An effect on the state covers every
   * exit there is, including ones nobody has written yet.
   */
  useEffect(() => {
    bridge.sendToGame('mining:visible', { visible: overlay === 'mining' });
  }, [bridge, overlay]);

  // Stable identity: the open panel registers its `Escape` handler against
  // this, and a new function every render would tear the listener down and
  // rebuild it on every keystroke the panel handles.
  const closeOverlay = useCallback(() => setOverlay(null), []);

  const changeCannon = useCallback(
    (rarity: CannonRarity, level: number) => {
      bridge.sendToGame('hunt:dev-cannon', { rarity, level });
    },
    [bridge],
  );

  // The arena appears only once the server has authorised a session: no
  // anonymous play, and no run that would later be refused.
  const bootstrapping = hunt.session === null;

  return (
    /*
     * The duel wraps the shell, and gives it back two things: a set of flags
     * and a way to open.
     *
     * `key` is the account ID, and it is the whole mechanism. A different
     * player is a different controller, so a socket, a pause lease or an
     * in-flight challenge can never cross from one identity to the next: React
     * unmounts the old feature, which disposes it, before the new one exists.
     *
     * The ID rather than the NAME. Two accounts may be called the same thing
     * and one account may rename itself, so keying on a name would have shared
     * a duel between two players and torn one down over a cosmetic change.
     * `PvpFeature` takes no account prop at all - one the component never read
     * would be decoration pretending to be a safeguard.
     *
     * Nothing below compares `state.kind`: every visual decision comes from
     * `presentation`, derived once in `pvpPresentation`.
     */
    <PvpFeature key={accountId} bridge={bridge} pause={pause} rebootstrapHunt={hunt.rebootstrap}>
      {({ presentation: pvp, open: openPvp }) => (
        <div className="app-shell">
          <TopBar
            snapshot={snapshot}
            fps={fps}
            debug={flags.debug}
            playerName={playerName}
            onSignOut={onSignOut}
            menuOpen={tabsOpen}
            onToggleMenu={() => setTabsOpen((open) => !open)}
            /*
             * Every tab in ONE row, handed to the bar to place.
             *
             * These used to be six independently positioned buttons, each at its
             * own offset from the centre of the arena. Equal centres are not equal
             * gaps when the labels differ in length, so the row drifted out of
             * alignment as soon as any label changed. A flex container spaces them
             * by their real widths and there is nothing left to keep in step by
             * hand.
             *
             * Built here rather than in the bar because what each button opens is
             * the app's business: the overlay state stays in one place instead of
             * six callbacks being threaded through a presentational component.
             *
             * Hidden as a whole when the player folds the menu away.
             */
            tabs={
              /*
               * Gone entirely during a duel.
               *
               * Not disabled: the hunt, the maps, the mine and the store all act on
               * a game that is paused and hidden, and a button that looks pressable
               * over a duel is a button somebody presses.
               */
              /*
               * E fora durante uma expedição, pela mesma razão que sai durante
               * um duelo: cada um destes botões age sobre uma caça que está
               * parada e escondida, e um botão que parece premível é um botão
               * que alguém prime.
               */
              tabsOpen && pvp.shellNavigationVisible && !huntOffScreen ? (
                <nav className="arena-tabs" id="arena-tabs" data-testid="arena-tabs">
                  {/* Opens the trade over the arena. The hunt keeps running behind
              it: the wave, the boss countdown and the shots in flight belong
              to the run, not to this dialogue. Opening the marketplace closes
              this one, and the reverse - they are alternatives. */}
                  <button
                    type="button"
                    className={`arena-tab trade-open-button${overlay === 'trade' ? ' is-active' : ''}`}
                    data-testid="trade-open"
                    aria-expanded={overlay === 'trade'}
                    onClick={() => setOverlay((current) => toggleOverlay(current, 'trade'))}
                  >
                    TRADE
                  </button>
                  <button
                    type="button"
                    className={`arena-tab market-open-button${overlay === 'market' ? ' is-active' : ''}`}
                    data-testid="market-open"
                    aria-expanded={overlay === 'market'}
                    onClick={() => setOverlay((current) => toggleOverlay(current, 'market'))}
                  >
                    MERCADO
                  </button>
                  <button
                    type="button"
                    className={`arena-tab gacha-open-button${overlay === 'gacha' ? ' is-active' : ''}`}
                    data-testid="gacha-open"
                    aria-expanded={overlay === 'gacha'}
                    onClick={() => setOverlay((current) => toggleOverlay(current, 'gacha'))}
                  >
                    GACHA
                  </button>
                  <button
                    type="button"
                    className={`arena-tab store-open-button${overlay === 'store' ? ' is-active' : ''}`}
                    data-testid="store-open"
                    aria-expanded={overlay === 'store'}
                    onClick={() => setOverlay((current) => toggleOverlay(current, 'store'))}
                  >
                    LOJA
                  </button>
                  {/* Where the ship may go. Floats over the arena like every other
              panel: opening it never resizes or recreates the canvas, and the
              hunt behind it keeps running. */}
                  <button
                    type="button"
                    className={`arena-tab maps-open-button${overlay === 'maps' ? ' is-active' : ''}`}
                    data-testid="maps-open"
                    aria-expanded={overlay === 'maps'}
                    onClick={() => {
                      setOverlay((current) => {
                        const next = toggleOverlay(current, 'maps');
                        if (next === 'maps') {
                          // Read on OPEN, never on a timer: unlocks change only when a
                          // boss dies, and that arrives inside a checkpoint answer the
                          // hook already handles.
                          void hunt.loadMaps();
                        }
                        return next;
                      });
                    }}
                  >
                    MAPAS
                  </button>
                  {/*
                   * Mining.
                   *
                   * Opening it reads the screen ONCE and tells the scene to draw;
                   * closing it tells the scene to drop its objects. Neither touches
                   * the canvas, the renderer or the hunt - the ring is a second scene
                   * in the same game, and what changes is which objects exist.
                   */}
                  <button
                    type="button"
                    className={`arena-tab mining-open-button${overlay === 'mining' ? ' is-active' : ''}`}
                    data-testid="mining-open"
                    aria-expanded={overlay === 'mining'}
                    onClick={() => {
                      setOverlay((current) => {
                        const next = toggleOverlay(current, 'mining');
                        if (next === 'mining') {
                          // Once, on open. Never on a timer: production is time on the
                          // server, and polling would not make it arrive sooner.
                          void hunt.loadMining();
                        }
                        return next;
                      });
                    }}
                  >
                    MINERAÇÃO
                  </button>
                  {/*
                   * The duel.
                   *
                   * Opening it costs the hunt nothing: the lobby floats, the arena
                   * keeps running and the pause only arrives when the SERVER says
                   * both seats are held. `pvpButtonEnabled` is the flag that says
                   * there is no panel open yet.
                   */}
                  <PvpOpenButton enabled={pvp.pvpButtonEnabled} onOpen={openPvp} />
                  {/*
                   * O mundo aberto.
                   *
                   * Abre um painel que flutua sobre a arena, como todos os
                   * outros: a caça atrás dele continua a correr, e confirmar a
                   * partida é o que a fecha — no servidor, e com o que foi
                   * jogado liquidado antes de fechar.
                   */}
                  <OpenWorldOpenButton
                    enabled={pvp.pvpButtonEnabled}
                    onOpen={() => setOpenWorldPanel((current) => !current)}
                  />
                </nav>
              ) : null
            }
            huntChipsVisible={pvp.huntHudVisible && !huntOffScreen}
            syncStatus={hunt.status}
            economic={hunt.economic}
          />
          {/* The arena spans the full width; every panel below floats over it. */}
          <main className="app-main">
            <div className="play-area">
              {manifestError ? (
                <div className="manifest-error" data-testid="manifest-error">
                  <p>Falha ao carregar os assets.</p>
                  <code>{manifestError}</code>
                </div>
              ) : hunt.status === 'maintenance' ? (
                /*
                 * The server shut the game while this player was in it.
                 *
                 * No arena here, and no notice either: the refusal was reported to
                 * `AvailabilityGate` by the API client in the same tick, so the
                 * whole tree - this component included - is being replaced by the
                 * full-screen notice. What this branch guarantees is only that no
                 * canvas exists in the meantime, because a canvas that survived one
                 * more frame would be a simulation running against a wallet a reset
                 * is rewriting.
                 */
                <div className="manifest-loading" data-testid="hunt-halted" />
              ) : hunt.status === 'failed' ? (
                <div className="manifest-error" data-testid="hunt-unavailable">
                  <p>Não foi possível carregar seu progresso.</p>
                  <code>Servidor indisponível. O progresso salvo está preservado.</code>
                </div>
              ) : manifest && hunt.session ? (
                <PhaserHost
                  bridge={bridge}
                  manifest={manifest}
                  debug={flags.debug}
                  bossFast={flags.bossFast}
                  rapidFire={flags.rapidFire}
                  session={hunt.session}
                />
              ) : (
                <div className="manifest-loading" data-testid="hunt-loading">
                  {bootstrapping ? 'Carregando progresso…' : 'Carregando assets…'}
                </div>
              )}

              {/* The 3D overlay is mounted only in DEV and only when 3D is the
              chosen renderer, so production never even imports Three.js. It
              floats over the arena and takes no pointer events. */}
              {/* No pet in a duel, ever - and the overlay reads the hunt's pet. */}
              {hunt.activePet &&
              !petRenderError &&
              hunt.session &&
              pvp.huntHudVisible &&
              !huntOffScreen ? (
                // No fallback element: while the chunk loads the arena is already
                // drawing the 2D pet, and swapping a placeholder in over the
                // canvas would be a flash for nothing.
                <Suspense fallback={null}>
                  <Pet3DOverlay
                    bridge={bridge}
                    activePet={hunt.activePet}
                    onUnavailable={petRenderUnavailable}
                  />
                </Suspense>
              ) : null}

              {/* Floats OVER the arena: reporting the failure must not unmount the
              canvas, which would destroy the very thing that is misbehaving. */}
              {sceneError ? (
                <div className="scene-error" data-testid="scene-error" role="alert">
                  <p>A cena não conseguiu adotar a sessão.</p>
                  <code>{sceneError}</code>
                </div>
              ) : null}

              {/*
               * The hunt's HUD, gone while a duel owns the screen.
               *
               * Level, Gold, health and the point button all describe a hunt that
               * is paused and hidden; leaving them up would be captioning a game
               * nobody is playing, over one they are.
               *
               * E gone durante uma **expedição**, pela mesma razão exata e não
               * por uma parecida: o mundo aberto segura a mesma trela de pausa
               * e manda esconder a mesma cena. A condição é repetida em cada
               * sítio em vez de derivada uma vez porque `pvp` só existe dentro
               * do render prop — e um sítio esquecido aqui é uma barra da caça a
               * flutuar sobre um quadrante, que foi exatamente o que a primeira
               * inspeção mostrou.
               */}
              {/*
               * A barra das cartas, com a MESMA condição do HUD da caça: ela
               * descreve uma caçada, e enquanto um duelo é dono do ecrã não há
               * caçada nenhuma para descrever.
               */}
              {pvp.huntHudVisible && !huntOffScreen ? (
                <CardHudBar
                  slots={cardSlots}
                  enabled={hunt.session !== null}
                  onActivate={(slot) => {
                    void hunt.activateCardSlot(slot);
                  }}
                  onRelease={(slot) => {
                    void hunt.releaseCardSlot(slot);
                  }}
                />
              ) : null}
              {pvp.huntHudVisible && !huntOffScreen ? (
                <HudPanel
                  snapshot={snapshot}
                  economy={hunt.economy}
                  onAllocate={(allocation) => {
                    void hunt.allocate(allocation);
                  }}
                  onDetonateBleed={() => {
                    void hunt.detonateBleeding();
                  }}
                />
              ) : null}
              {/*
               * The map screen.
               *
               * Rendered here rather than inside `FloatingOverlays` because it is
               * the one panel that is not self-contained: travelling settles the
               * hunt, so it has to reach the session hook. The others fetch their
               * own data and answer to nobody.
               *
               * Floats over the arena like the rest - opening it neither resizes
               * nor recreates the canvas, and the run behind it keeps playing.
               */}
              {overlay === 'maps' && hunt.maps ? (
                <MapsPanel
                  maps={hunt.maps.maps}
                  currentMap={hunt.maps.currentMap}
                  travelling={hunt.travelling}
                  onTravel={(mapIndex) => {
                    void hunt.travel(mapIndex);
                  }}
                  onClose={closeOverlay}
                  error={hunt.travelError}
                />
              ) : null}
              {/*
               * Mining.
               *
               * Beside the map screen and for the same reason: travelling settles
               * the hunt and mining reads the vault, so both need the session
               * hook. Floating like every other panel - opening it neither
               * resizes nor recreates the canvas, and the run behind it keeps
               * playing.
               */}
              {overlay === 'mining' ? (
                <MiningPanel
                  state={hunt.mining}
                  loading={hunt.miningLoading}
                  busy={hunt.miningBusy}
                  error={hunt.miningError}
                  notice={hunt.miningNotice}
                  onAssign={(instanceId) => {
                    void hunt.assignMining(instanceId);
                  }}
                  onRemove={(instanceId) => {
                    void hunt.removeMining(instanceId);
                  }}
                  onCollect={() => {
                    void hunt.collectMining();
                  }}
                  onCancel={() => {
                    void hunt.cancelMining();
                  }}
                  onSelect={(instanceId) => bridge.sendToGame('mining:select', { instanceId })}
                  onClose={closeOverlay}
                  /*
                   * Where the window left a hole for the scene to draw through.
                   *
                   * The stage is DOM and the planet is WebGL, and this is the one
                   * number that keeps them agreeing. Sent on mount and on every
                   * resize, so the planet follows the layout instead of being
                   * drawn where the layout used to be.
                   */
                  onStage={(rect) => bridge.sendToGame('mining:stage', rect)}
                />
              ) : null}
              {/* Exactly one of them, ever. */}
              <FloatingOverlays
                overlay={overlay}
                manifest={manifest}
                onClose={closeOverlay}
                onSettled={() => {
                  setInventoryRefresh((token) => token + 1);
                  /*
                   * The store moves Cash and VIP, so the HUD has to re-read them.
                   * A refresh, never a prediction: how much of a purchase reached
                   * the spendable balance depends on an outstanding debt, which
                   * only the server can settle.
                   */
                  void hunt.refreshEconomy();
                  /*
                   * And GOLD, which none of this used to touch.
                   *
                   * The HUD takes its Gold from the ARENA, which predicts it
                   * upwards from the kills it draws and lets a checkpoint replace
                   * the prediction. Nothing ever told it money had LEFT - so the
                   * gacha charged, its own panel showed the new balance because it
                   * re-read, and the chip beside the level went on advertising the
                   * old one until a checkpoint or a death happened to land.
                   */
                  void hunt.refreshProgression();
                }}
              />
              {/*
               * The hunt's metrics, gone with its HUD.
               *
               * Damage per shot, gold per minute and the overview toggle all read
               * the hunt's snapshot; over a duel they caption a game nobody is
               * playing, and the inventory they open belongs to it too.
               */}
              {pvp.huntHudVisible && !huntOffScreen ? (
                <MetricsPanel
                  snapshot={snapshot}
                  overviewOpen={overviewOpen}
                  onToggleOverview={() => setOverviewOpen((open) => !open)}
                  inventoryOpen={inventoryOpen}
                  onToggleInventory={() => setInventoryOpen((open) => !open)}
                />
              ) : null}
              {overviewOpen && snapshot ? (
                <OverviewPanel
                  snapshot={snapshot}
                  artifacts={hunt.artifacts}
                  activePet={hunt.activePet}
                  economy={hunt.economy}
                  onClose={() => setOverviewOpen(false)}
                />
              ) : null}
              {/* Rendered only while open, so a repeated click on the button
              toggles one panel rather than stacking another. The arena keeps
              running behind it: nothing here touches the canvas. */}
              {inventoryOpen && manifest ? (
                <InventoryPanel
                  loadout={hunt.loadout}
                  manifest={manifest}
                  refreshToken={inventoryRefresh}
                  /*
                   * O duelo VIVO desativa as ações de carta (gate V28H.G).
                   *
                   * Cortesia e não autoridade: o servidor recusa de qualquer
                   * maneira, e a recusa dele passa pela mesma frase. O sinal é
                   * a arena estar no ecrã - `pvpHudVisible` no modo integrado,
                   * `dedicatedMode` quando o duelo é a página inteira.
                   */
                  pvpActive={pvp.pvpHudVisible || pvp.dedicatedMode}
                  /* Uma caçada a correr não é bloqueada; é avisada. */
                  huntActive={hunt.session !== null}
                  onClose={() => setInventoryOpen(false)}
                  onEquip={async (itemId, loadoutVersion) => {
                    await hunt.equip(itemId, loadoutVersion);
                    // Only after the SERVER confirmed: the list, the badges and
                    // the details all reload from the vault, never optimistically.
                    setInventoryRefresh((token) => token + 1);
                  }}
                  onCombine={async (first, second) => {
                    await hunt.combine(first, second);
                    setInventoryRefresh((token) => token + 1);
                  }}
                  /*
                   * The confirmed slot move, handed straight to the run.
                   *
                   * No second bootstrap and no refresh token: the answer the
                   * wardrobe just received IS the authoritative reconfiguration
                   * - the same transaction settled the tail, moved the slot and
                   * rewrote this session's frozen loadout - so the arena adopts
                   * it directly. `hunt` is null when the account was not
                   * hunting, and then this does nothing at all.
                   *
                   * The item grid is deliberately not reloaded either: a skin is
                   * not in it, and the wardrobe already replaced its own view
                   * with the same response.
                   */
                  onSkinConfirmed={(response) => hunt.adoptSkinChange(response.hunt)}
                  pets={hunt.pets}
                  onActivatePet={async (petInstanceId) => {
                    await hunt.setActivePet(petInstanceId);
                    setInventoryRefresh((token) => token + 1);
                  }}
                  onEvolvePet={async (first, second) => {
                    await hunt.evolvePet(first, second);
                    setInventoryRefresh((token) => token + 1);
                  }}
                  onFeedPet={async (petInstanceId, foodDefinitionId) => {
                    await hunt.feedPet(petInstanceId, foodDefinitionId);
                    setInventoryRefresh((token) => token + 1);
                  }}
                  onUnlockPetAppearance={async (unlockDefinitionId) => {
                    const response = await hunt.unlockPetAppearance(unlockDefinitionId);
                    setInventoryRefresh((token) => token + 1);
                    // Handed straight back so the panel adopts the server's own
                    // palette instead of guessing or re-reading it.
                    return response;
                  }}
                  onApplyPetAppearance={async (petInstanceId, energyId, finishId) => {
                    await hunt.applyPetAppearance(petInstanceId, energyId, finishId);
                    setInventoryRefresh((token) => token + 1);
                  }}
                />
              ) : null}

              {/* Announced only after the server confirmed the drop, which is why
              nothing appears the instant a mob dies. Floats over the arena and
              never touches the canvas. */}
              {manifest ? (
                <DropToasts drops={hunt.drops} manifest={manifest} onDismiss={hunt.dismissDrop} />
              ) : null}

              {/* The special drops, on their own stack so a component and an
              artifact from the same window do not fight for one corner. */}
              {manifest ? (
                <EquipmentDropToasts
                  drops={hunt.equipmentDrops}
                  manifest={manifest}
                  onDismiss={hunt.dismissEquipmentDrop}
                />
              ) : null}

              {/* E as cartas, sem arte: os masters não existem, e o toast diz
              o nome e a raridade em texto para a queda não ficar em silêncio. */}
              <CardDropToasts drops={hunt.cardDrops} onDismiss={hunt.dismissCardDrop} />

              {/*
               * Which of the hunt's own overlays may be up, from one function.
               *
               * Both rules are about PvP and both were found in a screenshot;
               * `huntOverlayView` carries them, and its reasons, where a test
               * can reach them.
               */}
              {huntOverlayView({
                hasDeath: death !== null,
                hasOfflineReward: hunt.offlineReward !== null,
                pvpPanelOpen: pvp.panelOpen,
                pvpRestoring: pvp.restoring,
                openWorldActive: huntOffScreen,
              }).deathScreen && death ? (
                <DeathScreen
                  stats={death.stats}
                  cause={death.cause}
                  onRestart={() => {
                    // Restart goes through the server: it ends the old run, rolls
                    // a new boss timer and keeps every confirmed reward.
                    void hunt.restart();
                  }}
                />
              ) : null}
              {/*
               * The offline report WAITS while a PvP screen is up.
               *
               * A reload during a match asks the server two things at once: the
               * hunt's bootstrap, which may answer with an offline window, and
               * `GET /api/pvp/(team/)?…`, which answers with a match this
               * account is still in. The room holds the chair for twelve
               * seconds and the client is trying to spend them reconnecting -
               * and the offline dialog is a backdrop, so it covered the
               * reconnection and asked the player to read a report before they
               * could get back into a fight they were losing.
               *
               * So it is deferred, and deferred is the exact word: nothing is
               * dismissed, nothing is recalculated and nothing is granted
               * differently. `hunt.offlineReward` still holds the SAME object
               * the server sent, so the moment the PvP panel closes - which is
               * after the match ends, or the instant a player leaves a lobby -
               * this renders it unchanged. `dismissOfflineReward` remains the
               * only thing that clears it, and only a person can press it.
               *
               * `panelOpen` rather than `dedicatedMode` on purpose: the
               * reconnection screen is not dedicated yet, and it is the one
               * this was covering.
               */}
              {huntOverlayView({
                hasDeath: death !== null,
                hasOfflineReward: hunt.offlineReward !== null,
                pvpPanelOpen: pvp.panelOpen,
                pvpRestoring: pvp.restoring,
                openWorldActive: huntOffScreen,
              }).offlineReport && hunt.offlineReward ? (
                <OfflineRewardDialog
                  reward={hunt.offlineReward}
                  onClose={hunt.dismissOfflineReward}
                />
              ) : null}
              {/* Dev only, and hunt-only: every command it offers acts on the
                  hunt, so it goes with the rest of the hunt during a duel. */}
              {DEV_TOOLS_ALLOWED && flags.debug && pvp.huntHudVisible && !huntOffScreen ? (
                <DevPanel
                  rapidFire={rapidFire}
                  rarity={snapshot?.cannonRarity ?? 'excellent'}
                  level={snapshot?.cannonLevel ?? 1}
                  onCommand={sendDevCommand}
                  onCannonChange={changeCannon}
                  petRenderError={petRenderError}
                />
              ) : null}
              {/*
               * O mundo aberto: o painel de entrada, o HUD do quadrante ou o
               * relatório da expedição — o anfitrião decide qual dos três.
               * Flutua sobre a arena como todos os outros, portanto abri-lo
               * nunca redimensiona nem recria o canvas.
               */}
              <OpenWorldFeature
                session={openWorld}
                open={openWorldPanel}
                onClose={() => setOpenWorldPanel(false)}
                bridge={bridge}
                manifest={manifest}
              />
            </div>
          </main>
        </div>
      )}
    </PvpFeature>
  );
}
